import { faCalendar, faStar } from '@fortawesome/pro-regular-svg-icons'
import { faStar as faSolidStar } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import { Tooltip } from '@scoir/tooltips'
import { Link } from 'react-router-dom'

import { useAppSelector } from 'store'

import { encodeSelectedSchools } from '../encode-availability-link'
import { ExploreHighSchool } from './types'
import { ToggleTargeted } from './useTargetHighschoolsExplore'

interface ExploreToolbarProps {
  selectedSchools: string[]
  schoolData?: ExploreHighSchool[]
  toggleTargeted: ToggleTargeted
}

function createSelectedSchoolsQueryParam(ids: string[], schools?: ExploreHighSchool[]): string {
  if (!schools) return ''
  const selectedSchools = schools.filter((school) => ids.includes(school.id))
  const schoolList = selectedSchools.map(({ id, name, timeZone }) => ({ id, name, timeZone }))
  const earliestVisitDate = selectedSchools
    .map((school) => school.nextVisitDate)
    .filter(Boolean)
    .sort()[0]

  return encodeSelectedSchools(schoolList, earliestVisitDate)
}

const ExploreToolbar = ({ selectedSchools, schoolData, toggleTargeted }: ExploreToolbarProps) => {
  const hasSelection = selectedSchools.length > 0
  const theme = useTheme()
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('md'))
  const isPremiumExplorerEnabled = useAppSelector(
    (state) => state.college.record?.Features?.SupportsPremiumHighSchoolExplorer || false,
  )

  if (!hasSelection) return null

  return (
    <Box
      display="flex"
      whiteSpace="nowrap"
      alignItems="center"
      gridGap={8}
    >
      <Typography>{selectedSchools.length} Selected</Typography>
      {isMediumScreenSize ? (
        <Box
          display="flex"
          gridGap={8}
        >
          <Tooltip title="View Visit Calendar">
            <IconButton
              color="primary"
              aria-label="View Visit Calendar"
              disabled={selectedSchools.length === 0}
              component={Link}
              to={`${import.meta.env.BASE_URL}/recruit/visits/view/week?${createSelectedSchoolsQueryParam(
                selectedSchools,
                schoolData,
              )}`}
            >
              <FontAwesomeIcon icon={faCalendar} />
            </IconButton>
          </Tooltip>
          {isPremiumExplorerEnabled && (
            <>
              <Tooltip title="Mark as Targets">
                <IconButton
                  color="primary"
                  aria-label="Mark as Targets"
                  disabled={selectedSchools.length === 0}
                  onClick={() => toggleTargeted(selectedSchools.map((id) => ({ hsId: id, targeted: true })))}
                >
                  <FontAwesomeIcon icon={faSolidStar} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Unmark as Targets">
                <IconButton
                  color="primary"
                  aria-label="Unmark as Targets"
                  disabled={selectedSchools.length === 0}
                  onClick={() => toggleTargeted(selectedSchools.map((id) => ({ hsId: id, targeted: false })))}
                >
                  <FontAwesomeIcon icon={faStar} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ) : (
        <Box
          display="flex"
          gridGap={8}
        >
          <Button
            color="primary"
            variant="contained"
            disabled={selectedSchools.length === 0}
            component={Link}
            to={`${import.meta.env.BASE_URL}/recruit/visits/view/week?${createSelectedSchoolsQueryParam(
              selectedSchools,
              schoolData,
            )}`}
            startIcon={<FontAwesomeIcon icon={faCalendar} />}
          >
            View Visit Calendar
          </Button>
          {isPremiumExplorerEnabled && (
            <>
              <Button
                color="primary"
                variant="contained"
                disabled={selectedSchools.length === 0}
                onClick={() => toggleTargeted(selectedSchools.map((id) => ({ hsId: id, targeted: true })))}
                startIcon={<FontAwesomeIcon icon={faSolidStar} />}
              >
                Mark as Targets
              </Button>
              <Button
                color="primary"
                variant="outlined"
                disabled={selectedSchools.length === 0}
                onClick={() => toggleTargeted(selectedSchools.map((id) => ({ hsId: id, targeted: false })))}
                startIcon={<FontAwesomeIcon icon={faStar} />}
              >
                Unmark as Targets
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ExploreToolbar
